<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('stockReturn.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('home'), href: '/' },
        {
          text: $t('stockReturn.pageTitle'),
          active: true
        }
      ]" />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-flex ts-items-center ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
            <a-range-picker v-model="dateRange" :allowClear="false" format="DD-MM-YYYY" valueFormat="DD-MM-YYYY"
              style="width: 200px" @change="fetchData()" />
          </div>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <ts-input v-model="search" v-tooltip="$t('stockReturn.search')" :placeholder="$t('search')"
              style="width: 250px" class="inline-block" />
            <ts-filter ref="filter">
              <div class="row">
                <div class="col-md-12">
                  <label class="">{{ $t('stockReturn.branch') }}</label>
                  <ts-branch-filter @filter="branchFilter" class="ts-w-full"></ts-branch-filter>
                </div>
              </div>
              <div class="row ts-mt-3">
                <div class="col-md-12">
                  <label>{{ $t('stockReturn.warehouse') }}</label>
                  <ts-location-filter :isDefaultSelect="false" :showPlaceholder="false"
                    @filter="locationFilter"></ts-location-filter>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <Table
          :data="resources" 
          :columns="columns" 
          size="small"
          highlight-row
          stripe
          max-height="700"
          :loading="loading"
        >
          <template slot-scope="{ row }" slot="totalAmount">
              {{ $defaultCurrencyWithCode(row.total_amount) }}
          </template>
          <template slot-scope="{ row }" slot="action">
              <a href="#" v-tooltip="$t('quotation.reprint')" class="text-primary"
                @click.prevent="reprintReport(row)">
                <i class="fas fa-print"></i>
              </a>
              <a href="#" v-tooltip="$t('edit')" class="text-primary mr-2 ml-2" @click.prevent="edit(row)">
                <i class="fas fa-edit"></i>
              </a>
              <a-popconfirm placement="leftTop" :title="$t('are_you_sure_to_delete')" :ok-text="$t('yes')"
                :cancel-text="$t('no')" @confirm="deleteRecord(row)">
                <a href="#" :disabled="row._deleting">
                  <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
                  <i class="fa fa-trash text-red" v-else></i>
                </a>
              </a-popconfirm>
          </template>
        </Table>
      </div>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination v-model="pagination" @navigate="fetchData">
        </ts-pagination>
      </div>
      <a-modal :title="$t('pdf')" width="50%" v-model="show_pdf" :footer="null">
        <ts-preview-pdf v-model="waiting" :src="src" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import Reason from './components/reason_delete'

export default {
  name: 'stockReturn',
  data() {
    return {
      loading: false,
      show_pdf: false,
      waiting: false,
      src: null,
      errors: new Errors(),
      model: {
        deleted_reason: null
      }
    }
  },
  created() {
    this.$store.state.stock.stockReturn.dateRange = [
      moment().add(-7, 'days').format('DD-MM-YYYY'),
      moment().format('DD-MM-YYYY')
    ]
    this.fetchData()
  },
  computed: {
    ...mapState('stock/stockReturn', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.stock.stockReturn.search
      },
      set(newValue) {
        this.$store.commit('stock/stockReturn/SET_SEARCH', newValue)
        this.$store.commit('stock/stockReturn/RESET_CURRENT_PAGE')
      }
    },
    inactive: {
      get() {
        return this.$store.state.stock.stockReturn.isInActive
      },
      set(newValue) {
        this.$store.commit('stock/stockReturn/SET_IS_INACTIVE', newValue)
      }
    },
    dateRange: {
      get() {
        return this.$store.state.stock.stockReturn.dateRange
      },
      set(newValue) {
        this.$store.commit('stock/stockReturn/SET_DATE_RANGE', newValue)
      }
    },
    columns() {
      return [
        { 
          title: this.$t('stockReturn.branch'),
          key: 'branch_name_en' 
        },
        { 
          title: this.$t('stockReturn.date'),
          key: 'return_date' 
        },
        { 
          title: this.$t('stockReturn.reference'),
          key: 'reference_no' 
        },
        { 
          title: this.$t('stockReturn.stockReturnNumber'),
          key: 'return_number' 
        },
        { 
          title: this.$t('stockReturn.customer'),
          key: 'customer_name' 
        },
        { 
          title: this.$t('stockReturn.location'),
          key: 'location_name_en' 
        },
        { 
          title: this.$t('stockReturn.returnBy'),
          key: 'returned_name' 
        },
        { 
          title: this.$t('stockReturn.totalAmount'),
          slot: 'totalAmount',
          align: 'right'
        },
        {
					title: this.$t('actions'),
					key: 'action',
					slot: 'action',
					fixed: 'right',
					align: 'center',
					width: 100
				}
      ]
    }
  },
  methods: {
    addNew() {
      this.$router.push({ name: 'stock-return-create' })
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('stock/stockReturn/fetch', { ...attributes })
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    edit(record) {
      this.$store.commit('stock/stockReturn/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'stock-return-edit',
        params: { id: record.return_id }
      })
    },
    deleteRecord(record) {
      this.model.deleted_reason = null
      this.$confirm({
        title: this.$t('are_you_sure_to_delete'),
        content: h =>
          h(Reason, {
            props: {
              value: this.model,
              validate: this.errors,
              placeholder: this.$t('stockReturn.leaveReason')
            }
          }),
        okText: this.$t('yes'),
        okType: 'danger',
        cancelText: this.$t('no'),
        onOk: () => {
          return this.$store
            .dispatch('stock/stockReturn/destroy', {
              id: record.return_id,
              data: this.model
            })
            .then(response => {
              this.$toasted.success(response.message)
              this.fetchData()
              return Promise.resolve(response)
            })
            .catch(error => {
              this.$toasted.error(error.message)
              this.errors = new Errors(error.errors)
              return Promise.reject(error)
            })
        },
        onCancel() { }
      })
    },
    branchFilter(value) {
      this.$store.commit('stock/stockReturn/SET_BRANCH_ID', value)
      this.fetchData()
    },
    locationFilter(value) {
      this.$store.commit('stock/stockReturn/SET_LOCATION_ID', value)
      this.fetchData()
    },
    reprintReport(record) {
      this.show_pdf = true
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('stock/stockReturn/reprint', record.return_id)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    }
  },
  watch: {
    search: debounce(function () {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('stock/stockReturn/RESET_STATE')
    next()
  }
}
</script>
